import apiClient from './api';

export interface ExplainResponse {
  original: string;
  translation: string;
  breakdown: string;
  nuances: string;
  feel: string;
}

export interface PostExplainPayload {
  segmentId: number;
  transcript: string;
}

const explainerService = {
  /**
   * Fetches a saved explanation from DB.
   * Returns ExplainResponse if found, null if 404 (cache miss — caller should POST).
   * Throws on other errors (network, 429, 500, etc.).
   */
  async getExplanation(segmentId: number): Promise<ExplainResponse | null> {
    try {
      const response = await apiClient.get<ExplainResponse>(`/ai/explain/${segmentId}`, { silentStatuses: [404, 429] });
      return response.data;
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      if (axiosError?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Bulk-checks which segments have a saved explanation for the current user.
   * Returns a map of segmentId (string) -> boolean.
   * Single DB query — not rate-limited.
   */
  async getExplanationStatus(segmentIds: number[]): Promise<Record<string, boolean>> {
    if (segmentIds.length === 0) return {};
    const params = segmentIds.join(',');
    const response = await apiClient.get<Record<string, boolean>>(`/ai/explain/status?segmentIds=${params}`);
    return response.data;
  },

  /**
   * Calls Claude via the backend, saves result to DB, returns explanation.
   * Throws on error (including 429 rate limit — caller checks err.response.status).
   */
  async postExplanation(payload: PostExplainPayload): Promise<ExplainResponse> {
    const response = await apiClient.post<ExplainResponse>('/ai/explain', payload);
    return response.data;
  },
};

export default explainerService;
